import { Button } from '@shared/components/ui/button';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { Product } from '@shared/components/ui/Product';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';

interface ProductToLocate {
  id: string;
  name: string;
  code: string;
  quantity: number;
  totalQuantity: number;
  location?: string;
  image?: string;
}

interface LocatingProductBlockProps {
  product: ProductToLocate;
  currentLocation: {
    id: number;
    code: string;
  };
  quantityToLocate: number;
  onQuantityChange: (quantity: number) => void;
  onConfirm: () => void;
  isLocating?: boolean;
}

export const LocatingProductBlock = ({
  product,
  currentLocation,
  quantityToLocate,
  onQuantityChange,
  onConfirm,
  isLocating = false,
}: LocatingProductBlockProps) => {
  const { t } = useTranslation('receiveModule');

  // 🎯 Verificar si se puede ubicar (cantidad alcanzada)
  const canLocate = quantityToLocate >= product.quantity;

  return (
    <div
      className={`bg-card-bg border border-card-border rounded-lg p-4 space-y-4 transition-opacity duration-300 ${
        isLocating ? 'opacity-50 pointer-events-none' : 'opacity-100'
      }`}
    >
      {/* Header - Ubicando */}
      <div className="flex items-center justify-between">
        <h3 className="text-title text-sm font-medium">{t('locate_modal.locating')}</h3>
        <span
          className="inline-flex items-center gap-2 bg-card-border light:bg-badge-bg-status-1 text-badge-status-1 rounded-full px-3 py-1"
          data-testid="locating-location-badge"
        >
          <FontAwesomeIcon icon={faLocationDot} className="w-3 h-3" />
          <span className="font-medium">{currentLocation.code}</span>
        </span>
      </div>

      {/* Producto */}
      <div className="flex items-center justify-between">
        <Product
          id={parseInt(product.id)}
          src={product.image || '/imagen/example.webp'}
          name={product.name}
          code={product.code}
          className="flex-1"
        />

        <div className="flex items-center gap-2 bg-card-border light:bg-[#3D4A5D] rounded-full px-3 py-1">
          <span className="text-xs text-title light:text-white whitespace-nowrap">{product.quantity}</span>
        </div>
      </div>

      {/* Cantidad a ubicar con botón Ubicar */}
      <div className="flex items-center gap-2 justify-between">
        <div className="sm:w-[127px]">
          <StepperInput
            value={quantityToLocate}
            onChange={onQuantityChange}
            min={1}
            max={product.quantity}
            step={1}
            data-testid="quantity-stepper"
          />
        </div>
        <div>
          <Button
            variant="primary"
            onClick={onConfirm}
            data-testid="confirm-locate-button"
            disabled={!canLocate || isLocating}
          >
            {t('locate_modal.confirm_button')}
          </Button>
        </div>
      </div>
    </div>
  );
};
